import { defineComponent as _defineComponent } from 'vue'
import {
	AudioTechnology,
	MonetizationType,
	PresentationType,
	UpcomingRelease,
	VideoTechnology,
} from '@/@types/graphql-types';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore } from '@/helpers/composables/useStores';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxInfoModal',
  props: {
    title: null,
    offer: null,
    seasonNumber: null,
    episodeNumber: null,
    displayBuyBoxInfoModal: { type: Boolean }
  },
  emits: ["close"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'close', display: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	title: TitleDetail;
	offer: (TitleOfferFragment & { iconUrl?: string; bundleId?: number; bundleClearName?: string }) | UpcomingRelease;
	seasonNumber?: number | null;
	episodeNumber?: number | null;
	/** For SEO - persist elements in HTML using css styles, not inline styles from v-show */
	displayBuyBoxInfoModal?: boolean;
};

const BaseModalNew = () => import('@/components/modals/BaseModalNew.vue');

const { t, tc } = useI18n();
const { country, language } = useLanguageStore();





const videoTechnologyMapping = {
	DOLBY_VISION: 'Dolby Vision',
	HDR: 'HDR',
	HDR10: 'HDR10',
	HDR10_PLUS: 'HDR10+',
};

const audioTechnologyMapping = {
	DOLBY_5_POINT_1: 'Dolby 5.1',
	DOLBY_ATMOS: 'Dolby Atmos',
	_5_POINT_1: 'Generic 5.1',
};

const providerName = computed(() => {
	if ('bundleClearName' in props.offer && props.offer?.bundleClearName) return props.offer.bundleClearName;
	return props.offer?.package?.clearName;
});

const titleName = computed(() => {
	if (props.seasonNumber && props.episodeNumber)
		return `${props.title.content.title} - ${t('WEBAPP_SEASON_X_EPISODE_Y', {
			season: props.seasonNumber,
			episode: props.episodeNumber,
		})}`;

	const isSeason = props.title.__typename === 'Season' && !!props.title.show;
	return isSeason ? `${props.title.show.content.title} - ${props.title.content.title}` : props.title.content.title;
});

const isCinema = computed(() => {
	return props.offer.__typename === 'UpcomingRelease'
		? props.offer.releaseType === 'THEATRICAL'
		: (props.offer as TitleOfferFragment).monetizationType === MonetizationType.Cinema;
});

const heading = computed(() => {
	if (props.offer.__typename === 'Offer') {
		if (props.offer.presentationType === PresentationType.Dvd) {
			return t('WEBAPP_MORE_INFO_DVD', { title: titleName.value, provider: providerName.value });
		}
		if (props.offer.presentationType === PresentationType.Bluray) {
			return t('WEBAPP_MORE_INFO_BLURAY', { title: titleName.value, provider: providerName.value });
		}
	}
	if (isCinema.value) {
		return t('WEBAPP_MORE_INFO_TITLE_ON_CINEMA_PROVIDER', {
			titleName: titleName.value,
			provider: providerName.value,
		});
	}
	return t('WEBAPP_MORE_INFO_TITLE_ON_PROVIDER', {
		title: titleName.value,
		provider: providerName.value,
	});
});

const availableUntil = computed(() => {
	if (props.offer.__typename !== 'Offer') return '';
	const date = new Date(props?.offer?.availableTo);
	const isFutureDate = date > new Date();
	if (!isFutureDate) return false;
	const locale = `${language.value}-${country.value}`;
	return date.toLocaleDateString(locale);
});

const runtimeMins = computed(() => {
	if (!props.title.content.runtime) return null;
	return `${props.title.content.runtime}min`;
});

const ageRating = computed(() => {
	if (props.title.__typename !== 'Season' && props.title.content.ageCertification) {
		return props.title.content.ageCertification;
	}
	return null;
});

const streamQuality = computed(() => {
	if (props.offer.__typename !== 'Offer') return '';

	switch (props?.offer?.presentationType) {
		case PresentationType.Sd:
			return 'SD';
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'SD';
		case PresentationType.Bluray:
			return 'HD';
		case PresentationType.Bluray_4K:
			return '4K UHD ';
		default:
			return '';
	}
});

const audioLanguages = computed(() =>
	props.offer.__typename === 'Offer' ? languageArrayToString(props.offer?.audioLanguages) : ''
);

const subtitleLanguages = computed(() => {
	return props.offer.__typename === 'Offer' ? languageArrayToString(props?.offer?.subtitleLanguages) : '';
});

const videoTechnology = computed(() => {
	return props.offer.__typename === 'Offer'
		? props.offer?.videoTechnology
				.map((technology: VideoTechnology) => videoTechnologyMapping[technology])
				.join(', ')
		: '';
});

const audioTechnology = computed(() => {
	return props.offer.__typename === 'Offer'
		? props.offer?.audioTechnology
				.map((technology: AudioTechnology) => {
					return audioTechnologyMapping[technology];
				})
				.join(', ')
		: '';
});

const hasInfo = computed(() => {
	return (
		availableUntil.value ||
		runtimeMins.value ||
		ageRating.value ||
		streamQuality.value ||
		audioLanguages.value ||
		subtitleLanguages.value ||
		videoTechnology.value ||
		audioTechnology.value
	);
});

const isDvdBluRayOffer = computed(() => {
	return (
		props.offer.__typename === 'Offer' &&
		[PresentationType.Dvd, PresentationType.Bluray, PresentationType.Bluray_4K].includes(
			props.offer.presentationType
		)
	);
});

function languageArrayToString(languageArray: string[]) {
	return languageArray
		.map(language => tc(`WEBAPP_LANGUAGE_${language.toUpperCase()}`))
		.filter(language => !language.startsWith('WEBAPP_LANGUAGE_'))
		.join(', ');
}

return { __sfc: true,BaseModalNew, t, tc, country, language, props, emit, videoTechnologyMapping, audioTechnologyMapping, providerName, titleName, isCinema, heading, availableUntil, runtimeMins, ageRating, streamQuality, audioLanguages, subtitleLanguages, videoTechnology, audioTechnology, hasInfo, isDvdBluRayOffer, languageArrayToString }
}

})